import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtDate, fmtRelative, slugify } from "../lib/utils-app";
import { articleRegion } from "../lib/article-utils";
import AdBlock from "../components/AdBlock";
import Seo, { ld } from "../components/Seo";
import { TrendingUp, Activity, ShieldCheck, Sparkles, FileCheck2, Clock, Globe2 } from "lucide-react";
import Img from "../components/Img";

/* ============================================================ */
/* CARDS                                                        */
/* ============================================================ */

const TrustPills = ({ sourceCount = 0 }) => (
  <div className="flex items-center gap-1.5 flex-wrap">
    <span className="io-badge io-badge-blue" title="AI-assisted draft, human-reviewed"><Sparkles size={10} /> AI</span>
    <span className="io-badge io-badge-green" title="Reviewed by editorial team"><ShieldCheck size={10} /> Reviewed</span>
    {sourceCount > 0 && <span className="io-badge io-badge-gold" title={`${sourceCount} verified source${sourceCount > 1 ? "s" : ""}`}><FileCheck2 size={10} /> {sourceCount} src</span>}
  </div>
);

const CardMeta = ({ a }) => (
  <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--io-muted)]">
    <span className="text-[var(--io-gold)]">{a.category}</span>
    <span>·</span>
    <span>{articleRegion(a)}</span>
    <span>·</span>
    <span>{fmtRelative(a.published_at)}</span>
    <span>·</span>
    <span className="inline-flex items-center gap-1"><Clock size={10}/> {a.read_minutes || 4}m</span>
  </div>
);

const BreakingCard = ({ a }) => (
  <Link to={`/article/${a.slug}`} className="group block io-card overflow-hidden" data-testid={`breaking-${a.slug}`}>
    {a.hero_image && (
      <div className="aspect-[16/9] overflow-hidden border-b border-[var(--io-border)]">
        <Img src={a.hero_image} className="w-full h-full object-cover img-hover" w={600} />
      </div>
    )}
    <div className="p-5">
      <CardMeta a={a} />
      <h3 className="font-serif text-[22px] md:text-[24px] leading-[1.15] mt-2.5 text-[var(--io-text)] group-hover:text-[var(--io-gold)] transition-colors">{a.headline}</h3>
      <p className="mt-2 text-[14px] text-[var(--io-text-2)] line-clamp-2">{a.summary}</p>
      <div className="mt-4 flex items-center justify-between">
        <TrustPills sourceCount={(a.source_refs || []).length} />
        <span className="text-[11px] font-mono text-[var(--io-muted)] group-hover:text-[var(--io-gold)]">Read brief →</span>
      </div>
    </div>
  </Link>
);

const ListCard = ({ a }) => (
  <Link to={`/article/${a.slug}`} className="group block py-5 hairline-b" data-testid={`list-${a.slug}`}>
    <CardMeta a={a} />
    <h3 className="font-serif text-[18px] md:text-[20px] mt-1.5 text-[var(--io-text)] group-hover:text-[var(--io-gold)] transition-colors">{a.headline}</h3>
    <p className="mt-1.5 text-[13px] text-[var(--io-text-2)] line-clamp-2">{a.summary}</p>
  </Link>
);

const TickerBar = ({ items }) => (
  <div className="hairline-b bg-[var(--io-bg-2)]" data-testid="breaking-ticker">
    <div className="max-w-[1380px] mx-auto px-4 md:px-8 flex items-center gap-4 py-2">
      <div className="flex items-center gap-2 shrink-0">
        <span className="signal-dot-red signal-dot" aria-hidden="true" />
        <span className="eyebrow" style={{ color: "#EF4444" }}>Breaking</span>
      </div>
      <div className="overflow-hidden flex-1">
        <div className="ticker-track whitespace-nowrap text-[13px] font-sans">
          {[...items, ...items].map((t, i) => (
            <Link key={i} to={`/article/${t.slug}`} className="mx-6 text-[var(--io-text)] hover:text-[var(--io-gold)]" data-testid={`ticker-item-${i}`}>
              <span className="font-mono text-[11px] text-[var(--io-muted)] mr-2">{t.category.toUpperCase()}</span>
              {t.headline}
            </Link>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ============================================================ */
/* SIGNALS DASHBOARD (hero right panel)                         */
/* ============================================================ */

const SignalRow = ({ label, value, tone = "" }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-[var(--io-border)] last:border-b-0" data-testid={`signal-${label.toLowerCase().replace(/\s+/g,"-")}`}>
    <span className="text-[13px] font-sans text-[var(--io-text)]">{label}</span>
    <span className={`font-mono text-[15px] ${tone === "gold" ? "text-[var(--io-gold)]" : tone === "green" ? "text-[#10B981]" : "text-[var(--io-text)]"}`}>{value}</span>
  </div>
);

const SignalsPanel = ({ sig }) => (
  <aside className="io-card-el p-5 md:p-6 relative overflow-hidden" data-testid="signals-panel">
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="eyebrow">Live Signals</div>
        <h3 className="font-serif text-[22px] mt-1 text-[var(--io-text)]">Today's ODR & Arbitration</h3>
      </div>
      <Activity size={18} className="text-[var(--io-gold)]" />
    </div>
    <div>
      <SignalRow label="Global updates" value={sig.global_updates ?? 0} />
      <SignalRow label="India updates" value={sig.india_updates ?? 0} tone="gold" />
      <SignalRow label="Institutional updates" value={sig.institutional_updates ?? 0} />
      <SignalRow label="Award enforcement cases" value={sig.award_enforcement ?? 0} />
      <SignalRow label="ODR & digital developments" value={sig.odr_developments ?? 0} />
      <SignalRow label="Pending editorial review" value={sig.pending_review ?? 0} />
      <SignalRow label="AI confidence" value={`${sig.ai_confidence ?? 94}%`} tone="green" />
    </div>
    <div className="mt-5 pt-4 border-t border-[var(--io-border)] flex items-center justify-between text-[11px] font-mono text-[var(--io-muted)]">
      <span className="inline-flex items-center gap-1"><span className="signal-dot" /> Live feed</span>
      <span>Refreshed {new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
    </div>
  </aside>
);

/* ============================================================ */
/* PAGE                                                         */
/* ============================================================ */

export default function Home() {
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "IndiaODR — Global Arbitration & ODR Intelligence";
    api.get("/homepage").then((r) => setData(r.data)).catch(() => setData({ hero: null, picks: [], latest: [], bands: [], ticker: [], signals: {} }));
  }, []);

  if (!data) return <div className="max-w-[1380px] mx-auto px-6 py-28 text-[var(--io-muted)]">Loading intelligence feed…</div>;
  if (!data.hero) return <div className="max-w-[1380px] mx-auto px-6 py-28 text-[var(--io-muted)]">No articles yet.</div>;

  const hero = data.hero;

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        type="website"
        jsonLd={[
          ld.organization(typeof window !== "undefined" ? window.location.origin : ""),
          ld.website(typeof window !== "undefined" ? window.location.origin : ""),
        ]}
      />
      {data.ticker.length > 0 && <TickerBar items={data.ticker} />}

      {/* ==================== HERO ==================== */}
      <section className="intel-grid border-b border-[var(--io-border)]" data-testid="hero-section">
        <div className="max-w-[1380px] mx-auto px-4 md:px-8 py-12 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-5">
              <span className="eyebrow">Intelligence Platform</span>
              <span className="io-badge io-badge-gold"><TrendingUp size={11}/> Est. 2026</span>
            </div>
            <h1 className="font-serif text-[44px] md:text-[60px] lg:text-[68px] leading-[1.02] tracking-tight text-[var(--io-text)]">
              Global Arbitration<br/>&amp; <span className="text-[var(--io-gold)]">ODR</span> Intelligence
            </h1>
            <p className="mt-6 text-[17px] md:text-[18px] text-[var(--io-text-2)] max-w-[58ch] leading-relaxed">
              Track the latest arbitration news, award enforcement updates, institutional rule changes,
              Indian case law, mediation, conciliation, and online dispute resolution trends —
              explained with source-backed legal context.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/latest" className="btn btn-primary" data-testid="cta-latest">Read latest updates</Link>
              <Link to="/category/india-arbitration-odr" className="btn btn-ghost" data-testid="cta-india">Explore India ODR</Link>
              <Link to="/digest" className="btn btn-ghost" data-testid="cta-digest">Weekly digest</Link>
            </div>
            <div className="mt-7 flex flex-wrap gap-2" data-testid="hero-trust">
              <span className="io-badge io-badge-blue"><Sparkles size={11}/> AI-assisted</span>
              <span className="io-badge io-badge-green"><ShieldCheck size={11}/> Human reviewed</span>
              <span className="io-badge io-badge-gold"><FileCheck2 size={11}/> Source-backed</span>
              <span className="io-badge"><Globe2 size={11}/> Global coverage</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <SignalsPanel sig={data.signals || {}} />
          </div>
        </div>
      </section>

      {/* ==================== MAIN GRID ==================== */}
      <div className="max-w-[1380px] mx-auto px-4 md:px-8 py-12 md:py-16">
        {/* TOP STORY + PICKS */}
        <section className="grid lg:grid-cols-12 gap-8 lg:gap-12 mb-16" data-testid="top-section">
          <Link to={`/article/${hero.slug}`} className="lg:col-span-8 group block" data-testid="hero-article">
            {hero.hero_image && (
              <div className="aspect-[16/9] overflow-hidden border border-[var(--io-border)] rounded-lg mb-6">
                <Img src={hero.hero_image} className="w-full h-full object-cover img-hover" w={1200} priority />
              </div>
            )}
            <CardMeta a={hero} />
            <h2 className="font-serif text-[36px] md:text-[48px] lg:text-[54px] leading-[1.05] tracking-tight mt-3 text-[var(--io-text)] group-hover:text-[var(--io-gold)] transition-colors">{hero.headline}</h2>
            <p className="mt-5 text-[17px] md:text-[19px] text-[var(--io-text-2)] max-w-[64ch] leading-[1.65]">{hero.summary}</p>
            <div className="mt-5 flex items-center justify-between">
              <TrustPills sourceCount={(hero.source_refs || []).length} />
              <span className="font-mono text-[12px] text-[var(--io-muted)]">By {hero.author}</span>
            </div>
          </Link>

          <aside className="lg:col-span-4 lg:border-l lg:border-[var(--io-border)] lg:pl-10" data-testid="editors-picks">
            <div className="flex items-center justify-between mb-3">
              <h3 className="eyebrow">Editor's Briefing</h3>
              <Link to="/latest" className="text-[11px] font-mono text-[var(--io-gold)] hover:underline">All →</Link>
            </div>
            {data.picks.map((p) => (
              <Link key={p.id} to={`/article/${p.slug}`} className="block py-4 hairline-b group" data-testid={`pick-${p.slug}`}>
                <CardMeta a={p} />
                <h4 className="font-serif text-[17px] md:text-[18px] mt-1.5 text-[var(--io-text)] group-hover:text-[var(--io-gold)] leading-snug">{p.headline}</h4>
              </Link>
            ))}
          </aside>
        </section>

        {/* CATEGORY BANDS */}
        {data.bands.map((band, idx) => (
          <React.Fragment key={band.slug}>
            <section className="py-10 border-t border-[var(--io-border)]" data-testid={`band-${band.slug}`}>
              <div className="flex items-baseline justify-between mb-6">
                <div>
                  <div className="eyebrow">Intelligence Band</div>
                  <h3 className="font-serif text-[26px] md:text-[32px] mt-1 text-[var(--io-text)]">{band.category}</h3>
                </div>
                <Link to={`/category/${band.slug}`} className="text-[12px] font-mono text-[var(--io-gold)] hover:underline" data-testid={`band-more-${band.slug}`}>View all →</Link>
              </div>
              <div className="grid md:grid-cols-3 gap-5">
                {band.articles.map((a) => <BreakingCard key={a.id} a={a} />)}
              </div>
            </section>
            {idx === 2 && (
              <section className="py-6" data-testid="ad-home-mid">
                <AdBlock variant="banner" />
              </section>
            )}
          </React.Fragment>
        ))}

        {/* LATEST STRIP */}
        <section className="py-10 border-t border-[var(--io-border)]" data-testid="latest-strip">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="eyebrow">Feed</div>
              <h3 className="font-serif text-[26px] md:text-[32px] mt-1 text-[var(--io-text)]">Latest</h3>
            </div>
            <Link to="/latest" className="text-[12px] font-mono text-[var(--io-gold)] hover:underline">Full feed →</Link>
          </div>
          <div className="grid md:grid-cols-2 gap-x-12">
            {data.latest.slice(0, 8).map((a) => <ListCard key={a.id} a={a} />)}
          </div>
        </section>

        {/* NEWSLETTER */}
        <section className="mt-16 io-card-el p-8 md:p-12 relative overflow-hidden" data-testid="newsletter-cta">
          <div className="absolute inset-0 intel-grid opacity-40 pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="eyebrow" style={{ color: "#D4AF37" }}>Weekly Intelligence Digest</div>
              <h3 className="font-serif text-[28px] md:text-[36px] mt-2 text-[var(--io-text)] leading-tight">Every Friday. Ten minutes. The arbitration week, decoded.</h3>
              <p className="mt-3 text-[14px] text-[var(--io-text-2)] max-w-[54ch]">Curated by our editorial desk — India rulings, institutional changes, investor-state claims, ODR developments and explainers.</p>
            </div>
            <Link to="/digest" className="btn btn-primary md:justify-self-end" data-testid="newsletter-cta-btn">Subscribe to digest →</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
